import React, { useState } from 'react';
import { Filter, Grid, List, ChevronDown, Search as SearchIcon, Package, Loader } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { categories, products as mockProducts } from '../data/products';
import { useProducts } from '../hooks/useProducts';
import { supabase } from '../lib/supabase';

const ProductsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { products, loading: productsLoading } = useProducts();

  // Parse search query from URL on initial load
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get('search');
    if (searchParam) {
      setSearchQuery(searchParam);
      handleSearch(searchParam);
    } else {
      setFilteredProducts(products.length > 0 ? products : mockProducts);
    }
  }, [location.search, products, mockProducts]);

  // Handle search
  const handleSearch = async (query: string) => {
    try {
      setLoading(true);
      setError(null);
      
      if (!query.trim()) {
        setFilteredProducts(products.length > 0 ? products : mockProducts);
        return;
      }
      
      // Try database search first, fallback to mock data
      try {
        const { data, error } = await supabase
          .from('products')
          .select(`
            *,
            categories:category_id(name),
            subcategories:subcategory_id(name),
            suppliers:supplier_id(organization, location)
          `)
          .or(`name.ilike.%${query}%,description.ilike.%${query}%`);
        
        if (error) throw error;
        
        if (data && data.length > 0) {
          const transformedProducts = data.map((product: any) => ({
            ...product,
            quantity_prices: product.quantity_prices || [],
            category_name: product.categories?.name,
            subcategory_name: product.subcategories?.name,
            supplier_name: product.suppliers?.organization,
            supplier_location: product.suppliers?.location
          }));
          
          setFilteredProducts(transformedProducts);
        } else {
          // Search in mock data
          const searchResults = mockProducts.filter(product =>
            product.name.toLowerCase().includes(query.toLowerCase()) ||
            (product.description && product.description.toLowerCase().includes(query.toLowerCase()))
          );
          setFilteredProducts(searchResults);
        }
      } catch (dbError) {
        console.warn('Database search failed, using mock data:', dbError);
        const searchResults = mockProducts.filter(product =>
          product.name.toLowerCase().includes(query.toLowerCase()) ||
          (product.description && product.description.toLowerCase().includes(query.toLowerCase()))
        );
        setFilteredProducts(searchResults);
      }
    } catch (err) {
      console.error('Error searching products:', err);
      setError('Failed to search products. Please try again.');
      setFilteredProducts(products.length > 0 ? products : mockProducts);
    } finally {
      setLoading(false);
    }
  };

  // Handle filter changes
  React.useEffect(() => {
    const sourceProducts = products.length > 0 ? products : mockProducts;
    if (sourceProducts.length === 0) return;
    
    let filtered = [...sourceProducts];
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => {
        const categorySlug = p.category_name?.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '');
        return categorySlug === selectedCategory;
      });
    }
    
    // Filter by price range
    if (priceRange !== 'all') {
      if (priceRange === '0-25') {
        filtered = filtered.filter(p => p.price <= 25);
      } else if (priceRange === '25-50') {
        filtered = filtered.filter(p => p.price > 25 && p.price <= 50);
      } else if (priceRange === '50-100') {
        filtered = filtered.filter(p => p.price > 50 && p.price <= 100);
      } else if (priceRange === '100+') {
        filtered = filtered.filter(p => p.price > 100);
      }
    }
    
    // Sort products
    if (sortBy === 'price-low') {
      filtered = [...filtered].sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-high') {
      filtered = [...filtered].sort((a, b) => b.price - a.price);
    } else if (sortBy === 'rating') {
      filtered = [...filtered].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'newest') {
      filtered = [...filtered].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }
    
    setFilteredProducts(filtered);
  }, [selectedCategory, priceRange, sortBy, products, mockProducts, searchQuery === '']);

  // Submit search form
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch(searchQuery);
    
    // Update URL with search query
    navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
  };

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    ...categories.map(cat => ({
      value: cat.slug,
      label: cat.name
    }))
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">All Products</h1>
          <p className="text-gray-600">Discover authentic products made in Bangladesh</p>
        </div>

        {/* Filters and Controls */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
            {/* Search Bar */}
            <div className="w-full lg:max-w-md">
              <form onSubmit={handleSearchSubmit} className="relative">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                <button 
                  type="submit"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-orange-600 text-white p-1 rounded-full hover:bg-orange-700"
                >
                  <SearchIcon className="h-4 w-4" />
                </button>
              </form>
              
              {/* Filter Button (Mobile) */}
              <button
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                className="mt-2 flex items-center space-x-2 bg-gray-100 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors lg:hidden"
              >
                <Filter className="h-4 w-4" />
                <span>Filters</span>
              </button>
            </div>

            {/* Desktop Filters */}
            <div className="hidden lg:flex items-center space-x-6">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-madeinbd-500"
              >
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>

              <select
                value={priceRange}
                onChange={(e) => setPriceRange(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-madeinbd-500"
              >
                <option value="all">All Prices</option>
                <option value="0-25">$0 - $25</option>
                <option value="25-50">$25 - $50</option>
                <option value="50-100">$50 - $100</option>
                <option value="100+">$100+</option>
              </select>
            </div>

            {/* Sort and View Controls */}
            <div className="flex items-center space-x-4">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-madeinbd-500"
              >
                <option value="featured">Featured</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Highest Rated</option>
                <option value="newest">Newest</option>
              </select>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-madeinbd-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-madeinbd-600 text-white' : 'bg-gray-100 text-gray-600'}`}
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Mobile Filters */}
          {isFilterOpen && (
            <div className="lg:hidden mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 gap-4">
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-madeinbd-500"
                >
                  {categoryOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>

                <select
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-madeinbd-500"
                >
                  <option value="all">All Prices</option>
                  <option value="0-25">$0 - $25</option>
                  <option value="25-50">$25 - $50</option>
                  <option value="50-100">$50 - $100</option>
                  <option value="100+">$100+</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Results Info */}
        <div className="mb-6">
          <p className="text-gray-600">
            Showing {filteredProducts.length} products
            {selectedCategory !== 'all' && (
              <span className="ml-2 text-madeinbd-600 font-medium">
                in {categoryOptions.find(c => c.value === selectedCategory)?.label}
              </span>
            )}
          </p>
        </div>

        {/* Products Grid */}
        {(loading || productsLoading) ? (
          <div className="flex justify-center items-center py-12">
            <Loader className="h-8 w-8 text-orange-500 animate-spin" />
            <span className="ml-2 text-gray-600">Searching products...</span>
          </div>
        ) : error ? (
          <div className="bg-red-50 p-4 rounded-lg text-center">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-red-700">{error}</p>
            <button 
              onClick={() => setFilteredProducts(products)}
              className="mt-2 text-orange-600 hover:text-orange-700 font-medium"
            >
              Reset Search
            </button>
          </div>
        ) : (
          <div className={`grid gap-4 md:gap-6 ${
            viewMode === 'grid' 
              ? 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4' 
              : 'grid-cols-1'
          }`}>
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* Load More */}
        {filteredProducts.length >= 20 && (
          <div className="text-center mt-12">
            <button className="bg-madeinbd-gradient text-white px-8 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity">
              Load More Products
            </button>
          </div>
        )}

        {/* No Products Found */}
        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600 mb-4">
              Try adjusting your filters or search criteria to find more products.
            </p>
            <button 
              onClick={() => {
                setSelectedCategory('all');
                setPriceRange('all');
                setSortBy('featured');
              }}
              className="bg-madeinbd-600 text-white px-6 py-2 rounded-lg hover:bg-madeinbd-700 transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductsPage;